/* eslint-disable jsx-a11y/heading-has-content */
import axiosInstance from '@/api/axiosConfig';
import TRXResponseDarkLogo from '@/assets/images/logo/trx-favicon-dark.svg?react';
import TRXResponseLogo from '@/assets/images/logo/trx-favicon.svg?react';
import FeedbackContainer from '@/components/chat/FeedbackContainer';
import { useAuth } from '@/hooks/useAuth';
import { useChatStore } from '@/hooks/useChatStore';
import { useProfile } from '@/hooks/useProfile';
import ChatErrorMessage from '@/pages/chat/components/ChatErrorMessage';
import DeepLinkButton from '@/pages/chat/components/DeepLinkButton';
import DocumentsList from '@/pages/chat/components/DocumentsList';
import TypingLoader from '@/pages/chat/components/TypingLoader';
import { getMarkdownProps } from '@/pages/chat/components/markdownProps';
import { colors } from '@/theme/colors';
import { isDemoEnvironment } from '@/util/demoUtils';
import { isStaffMode, isStaffConversationId } from '@/util/staffUtils';
import { fullName } from '@/util/util';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import KeyboardVoiceOutlinedIcon from '@mui/icons-material/KeyboardVoiceOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import GraphRenderer from '@/pages/chat/components/GraphRenderer';
type props = {
  chatId: string;
  isStreaming: boolean;
  streamingText: string;
  showTypingIndicator: boolean;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  handleSendMessage: (message: string) => void;
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ChatHistory: React.FC<props> = ({
  chatId,
  isStreaming,
  streamingText,
  showTypingIndicator,
  setInput,
  handleSendMessage,
}) => {
  const { authState } = useAuth();
  const { t } = useTranslation(['chat']);
  const { t: tVoiceMode } = useTranslation(['voiceMode']);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const { profile, profileImage } = useProfile();
  const theme = useTheme();
  const {
    chatHistory,
    totalPages,
    currentPage,
    setCurrentPage,
    setChatHistory,
  } = useChatStore();

  const [showNoMoreMessages, setShowNoMoreMessages] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const markdownProps = getMarkdownProps(theme, handleSendMessage);
  const isInitialLoad = useRef(true);
  const lastMessageId = useRef<string | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const isDemo = isDemoEnvironment();
  const isStaff = isStaffMode();

  useLayoutEffect(() => {
    if (
      chatContainerRef.current &&
      (isStreaming === false || showTypingIndicator === true)
    ) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [chatHistory, streamingText, isStreaming]);

  useEffect(() => {
    const fetchPaginationMessages = async () => {
      try {
        setLoadingChat(true);
        if (!isDemo && !isStaffConversationId(chatId) && currentPage > 1) {
          const response = await axiosInstance.get('/user/fetchAllMessage', {
            baseURL: process.env.REACT_APP_OCHESTRATOR_API_URL,
            headers: authState?.idToken?.idToken
              ? {
                  Authorization: `Bearer ${authState?.idToken?.idToken}`,
                }
              : undefined,
            params: {
              userId: profile?.value[0].eXpMember.Uuid as string,
              conversationId: chatId,
              pageNumber: currentPage,
              pageSize: 10,
            },
          });

          if (response.data) {
            const updateMessage = [...response.data.data.items, ...chatHistory];
            setChatHistory(updateMessage);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingChat(false);
      }
    };
    fetchPaginationMessages();
  }, [currentPage, isDemo]);
  useEffect(() => {
    if (lastMessageId.current) {
      const lastMessageElement = document.getElementById(lastMessageId.current);
      if (lastMessageElement) {
        lastMessageElement.scrollIntoView({
          behavior: 'auto',
          block: 'start',
        });
      }
      lastMessageId.current = null;
    }
  }, [chatHistory]);
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    const container = chatContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;

    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setShowScrollButton(!isAtBottom);

    if (scrollTop === 0 && !loadingChat && currentPage === totalPages) {
      setShowNoMoreMessages(true);
      const timer = setTimeout(() => {
        setShowNoMoreMessages(false);
      }, 2500);
      return () => clearTimeout(timer);
    }

    if (scrollTop === 0 && !loadingChat && currentPage < totalPages) {
      lastMessageId.current = sortedChats[0].id;
      setCurrentPage(currentPage + 1);
    }
  };

  const sortedChats = [...chatHistory].sort(
    (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
  );
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };
  const fallbackResponse: string =
    'I sincerely apologize, but it appears the connection was interrupted while I was generating your answer. Please try asking your question again, or briefly rephrase your query to ensure a successful response.';
  return (
    <>
      <Box
        ref={chatContainerRef}
        data-testid="chat-history"
        flex={1}
        width="100%"
        position="relative"
        sx={{
          padding: {
            xs: '10px 8px',
            sm: '10px 15px',
            md: '20px 24px',
            lg: '20px 92px',
          },
          overflow: 'auto',
          scrollBehavior:
            lastMessageId === null
              ? isInitialLoad.current
                ? 'instant'
                : 'smooth'
              : '',
          '&::-webkit-scrollbar': {
            width: '6px',
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[500],
            borderRadius: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.grey[300],
            borderRadius: '8px',
          },
        }}
        onScroll={handleScroll}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={1}
          sx={{ color: theme.palette.chatText.main }}
        >
          {loadingChat && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                zIndex: 9999999,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                border: 1,
                borderColor: theme.palette.grey[400],
                borderRadius: '10px',
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2">{t('loadingMsgs')}</Typography>
            </Box>
          )}
          {showNoMoreMessages && (
            <Alert
              severity="info"
              variant="outlined"
              sx={{
                textAlign: 'center',
                position: 'absolute',
                top: 10,
                zIndex: 9999999,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              {t('noMoreMsgs')}
            </Alert>
          )}
        </Box>
        {sortedChats.map((chat, index) => {
          const shouldRenderExtras =
            (index !== chatHistory.length - 1 || !isStreaming) &&
            chat.response.trim() !== 'In progress...' &&
            chat.response.trim() !== '' &&
            !chat.error;
          return (
            <Box
              id={chat.id}
              key={chat.id}
              data-testid={`chat-message-${chat.id}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: 'column',
              }}
            >
              <Box
                display="flex"
                justifyContent="flex-end"
                width="100%"
                sx={{
                  gap: { xs: '6px', sm: '8px', md: '12px', lg: '20px' },
                  marginBottom: {
                    xs: '20px',
                    sm: '30px',
                    md: '50px',
                    lg: '70px',
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  color="textPrimary"
                  sx={{
                    fontSize: '16px',
                    color: theme.palette.chatText.main,
                    maxWidth: { xs: '82%', sm: '85%', md: '75%', lg: '70%' },
                    backgroundColor: 'rgba(226, 227, 228, 0.39)',
                    padding: {
                      xs: '5px 8px',
                      sm: '6px 10px',
                      md: '7px 12px',
                      lg: '8px 15px',
                    },
                    borderRadius: '20px',
                    animation: 'fadeIn 250ms ease-out',
                    '@keyframes fadeIn': {
                      from: { opacity: 0 },
                      to: { opacity: 1 },
                    },
                    textAlign: 'left',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {chat.chatType === 'Voice Mode' && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                      }}
                    >
                      <KeyboardVoiceOutlinedIcon
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: '14px',
                        }}
                      />
                      <Typography variant="caption">
                        {tVoiceMode('voiceInput')}
                      </Typography>
                    </Box>
                  )}
                  {chat.messageContent}
                </Typography>
                <Avatar
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: 'white',
                    height: { xs: '24px', sm: '28px', md: '30px', lg: '32px' },
                    width: { xs: '24px', sm: '28px', md: '30px', lg: '32px' },
                    marginTop: { xs: '2px', sm: '3px' },
                    flexShrink: 0,
                  }}
                  src={isDemo ? '' : profileImage}
                >
                  {isDemo ? 'D' : fullName(profile)?.charAt(0) || ''}
                </Avatar>
              </Box>
              {chat.error ? (
                <ChatErrorMessage
                  input={chat.messageContent}
                  setInput={setInput}
                />
              ) : (
                <Typography
                  position="relative"
                  variant="subtitle1"
                  sx={{
                    width: '100%',
                    minWidth: '24px',
                    color: theme.palette.chatText.main,
                    textAlign: 'left',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: {
                      xs: '13px',
                      sm: '14px',
                      md: '15px',
                      lg: '16px',
                    },
                    animation: `${fadeIn} 300ms ease-in-out`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: { xs: '6px', sm: '8px', md: '12px', lg: '20px' },
                    '& span': {
                      transition: 'all 0.2s ease-out',
                    },
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      '& svg': {
                        width: {
                          xs: '20px',
                          sm: '22px',
                          md: '24px',
                          lg: '26px',
                        },
                        height: {
                          xs: '20px',
                          sm: '22px',
                          md: '24px',
                          lg: '26px',
                        },
                        animation: `${fadeIn} 50ms ease-in-out forwards`,
                      },
                    }}
                  >
                    {theme.palette.mode === 'dark' ? (
                      <TRXResponseDarkLogo />
                    ) : (
                      <TRXResponseLogo key={index} />
                    )}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      width: '100%',
                      maxWidth: '100%',
                      marginBottom: {
                        xs: '30px',
                        sm: '40px',
                        md: '50px',
                        lg: '70px',
                      },
                    }}
                  >
                    {chat.chatType === 'Voice Mode' && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <VolumeUpOutlinedIcon
                          sx={{
                            color: theme.palette.primary.main,
                            fontSize: '14px',
                          }}
                        />
                        <Typography variant="caption">
                          {tVoiceMode('voiceOutput')}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      id={`chat-ans-${chat.id}`}
                      sx={{
                        width: '100%',
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      {index === chatHistory.length - 1 && isStreaming ? (
                        showTypingIndicator ? (
                          <TypingLoader />
                        ) : (
                          <Box
                            sx={{
                              minHeight: '50px',
                              width: '100%',
                              minWidth: 0,
                              maxWidth: '100%',
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            <ReactMarkdown {...markdownProps}>
                              {streamingText}
                            </ReactMarkdown>
                          </Box>
                        )
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            minWidth: 0,
                            maxWidth: '100%',
                            overflow: 'hidden',
                          }}
                        >
                          <ReactMarkdown {...markdownProps}>
                            {chat.response.trim() === 'In progress...'
                              ? fallbackResponse
                              : chat.response.trim()}
                          </ReactMarkdown>
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        width: {
                          xs: '100%',
                          sm: '100%',
                          md: '100%',
                          lg: '100%',
                        },
                        minWidth: 0,
                        // maxWidth: '100%',
                        overflow: 'hidden',
                      }}
                    >
                      {chat.documents &&
                        chat.documents.length > 0 &&
                        (index !== chatHistory.length - 1 || !isStreaming) && (
                          <DocumentsList documents={chat.documents} />
                        )}
                    </Box>
                    {shouldRenderExtras && (
                      <GraphRenderer
                        isGraph={chat.is_graph || !!chat.graphData}
                        question={chat.messageContent}
                        answer={chat.response}
                        sqlQuery={chat.sql_query}
                        tableName={chat.table_used}
                        sqlResult={chat.sql_result}
                        savedGraphData={chat.graphData}
                        messageId={chat.messageId || chat.id || chat.uId}
                        conversationId={chatId}
                      />
                    )}

                    {shouldRenderExtras && (
                      <FeedbackContainer
                        chatId={chat.messageId || chat.id || chat.uId}
                        conversationId={chatId}
                        responseText={chat.response}
                        feedback={chat.feedback?.type}
                        isStaffMode={isStaff}
                      />
                    )}

                    {shouldRenderExtras && chat.deepLink && (
                      <>
                        <Typography
                          sx={{
                            fontSize: { xs: '10px', sm: '11px', md: '12px' },
                            color: theme.palette.text.primary,
                            fontFamily: 'Roboto, Regular',
                            margin: { xs: '3px 0', sm: '4px 0', md: '6px 0' },
                          }}
                        >
                          {t('resource')}
                        </Typography>
                        <DeepLinkButton
                          deepLinkId={chat.deepLink}
                          messageObj={chat}
                        />
                      </>
                    )}
                  </Box>
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
      {showScrollButton && (
        <IconButton
          aria-label="scroll to bottom"
          sx={{
            position: 'absolute',
            bottom: '130px',
            padding: '2px',
            margin: 0,
            borderWidth: 1,
            borderStyle: 'solid',
            backgroundColor:
              theme.palette.mode !== 'dark'
                ? colors.myExpResourcesButton.dark.background
                : colors.myExpResourcesButton.light.background,
            ':hover': {
              backgroundColor:
                theme.palette.mode !== 'dark'
                  ? colors.myExpResourcesButton.dark.background
                  : colors.myExpResourcesButton.light.background,
            },
          }}
          onClick={scrollToBottom}
        >
          <ArrowDownwardRoundedIcon
            sx={{
              width: 24,
              height: 24,
              color: theme.palette.primary.main,
            }}
          />
        </IconButton>
      )}
    </>
  );
};

export default memo(ChatHistory);
